import {
  FC,
  useState,
  FocusEvent,
  KeyboardEvent,
  useRef,
  useEffect,
} from 'react';
import { Root } from '@radix-ui/react-dialog';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import useCreateProject from '../api/mutations/useCreateProject';
import useDeleteProject from '../api/mutations/useDeleteProject';
import useUpdateProjectName from '../api/mutations/useUpdateProjectName';
import useProjects from '../api/queries/useProjects';
import ValueguardLogo from '../assets/images/valueguard-icon-main.webp';
import MapstatLogo from '../assets/images/valueguard-icon-slate-900.webp';
import UserAvatar from '../assets/images/useravatar.webp';
import { DropDown, Line, Button, Dialog } from '../components';
import { useAuth } from '../context/AuthContext';
import { useMapContext } from '@/context/MapContext';
import { useProjectContext } from '../context/ProjectContext';
import { ButtonColor, ButtonSize, Icons, TabsType } from '../enums';
import strings from '../language/Swedish';
import { PATH } from '../routes/Paths';
import useUser from '../api/queries/useUser';
import gdprPDF from "@/assets/pdfs/gdpr.pdf";

const StyledSidebar = styled.aside<{ $isFolded: boolean }>`
  ${tw`hidden md:flex flex-col h-screen flex-col h-screen p-2 z-10 bg-white/40 backdrop-blur-xl border-r border-white/80 transition-all ease-[cubic-bezier(0.16,1,0.3,1)] duration-500`};

  ${({ $isFolded }) => ($isFolded ? tw`w-[calc(5rem-1px)]` : tw`w-64`)}
`;

const CloseButton = styled(Button)`
  ${tw`absolute w-4 -right-4 inset-y-0 flex items-center opacity-0 justify-end text-slate-800/50 transition rounded-full`};

  &:hover {
    ${tw`text-slate-800/100`};
  }

  &:after {
    ${tw`content-[''] absolute z-0 inset-px rounded-full border border-slate-500/20 bg-slate-500/10 opacity-0 transition`};
  }

  &:after,
  &:hover {
    ${tw`opacity-50`};
  }

  &:active,
  &:after {
    ${tw`opacity-100`};
  }
`;

const Input = styled.input`
  ${tw`my-0.5 px-2 py-0.5 bg-slate-700 w-full px-1 text-white text-xs rounded-lg border border-slate-300 shadow-slate-300 outline-none`};

  &:focus {
    ${tw`shadow-[0_0_0_4px_rgb(var(--color-primary)_/_0.2)]`};
  }
`;

const TopContainer = styled.div`
  ${tw`flex-none overflow-hidden pl-1`};
`;

const ProjectsContainer = styled.div`
  ${tw`flex flex-col flex-1 overflow-hidden`};
`;

const ProjectsList = styled.ul`
  ${tw`flex-1 overflow-y-auto max-h-full`};
`;

const Heading = styled.div<{ $isFolded: boolean }>`
  ${tw`flex items-center justify-between mb-3 rounded-lg truncate transition`};

  ${({ $isFolded }) => ($isFolded ? tw`justify-center` : tw`justify-between`)};
`;

const ProjectItem = styled.li<{ $isSelected: boolean }>`
  ${tw`w-full mb-1 px-2 py-0.5 rounded-lg border flex items-center justify-start text-left transition`};

  ${({ $isSelected }) =>
    $isSelected
      ? tw`bg-slate-800 border-slate-900 text-slate-50`
      : tw`bg-transparent border-transparent text-slate-800`};

  button {
    ${tw`opacity-0 border border-transparent transition`};

    &:hover {
      ${tw`opacity-100 border-slate-50`};

      ${({ $isSelected }) => $isSelected && tw`text-slate-50 `};
    }
  }

  &:hover {
    button {
      ${tw`opacity-100`};
    }

    ${({ $isSelected }) => !$isSelected && tw`bg-slate-800/5`};
  }

  &:active {
    ${tw`outline outline-slate-800/20 outline-offset-2`};
  }

  &:focus {
    ${tw`outline-primary`};
  }
`;

const ProjectLink = styled.div<{ $isSelected: boolean }>`
  ${tw`flex-1 pr-1 text-xs whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer`};

  ${({ $isSelected }) =>
    $isSelected ? tw`text-slate-50` : tw`text-slate-800`};
`;

const BottomContainer = styled.div<{ $isFolded: boolean }>`
  ${tw`flex-none overflow-hidden`};

  span {
    ${({ $isFolded }) => ($isFolded ? tw`hidden` : tw`inline-block`)};
  }

  img {
    ${({ $isFolded }) => ($isFolded ? tw`m-auto` : tw`inline-block`)};
  }
`;

const TopLine = styled(Line)`
  ${tw`mt-[0.5rem]`};
`;

const ButtomLine = styled(Line)`
  ${tw`mb-[0.5rem]`};
`;

const SUCCESS = 'success';

interface Props {
  defaultFolded?: boolean;
  save: (force: boolean) => void;
}
const sideBarProjectLimit = 40; // use 40 to cover the whole sidebar on big screens

const Sidebar: FC<Props> = ({ defaultFolded = false, save }) => {
  const [isFolded, setIsFolded] = useState<boolean>(defaultFolded);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [editName, setEditName] = useState<string | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<string>('');

  const { id } = useParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { logout, setIsCookieConsentOpen } = useAuth();
  const { resetMap, setIsStreetViewOn } = useMapContext();
  const { resetProject, setProject, setIsAdvancedFilterOpen,setSelectedTab } = useProjectContext();

  const { data: userData } = useUser();

  const {
    data: projectsData,
    isPending,
    isError,
    refetch: refetchProjects,
  } = useProjects(sideBarProjectLimit);

  const { mutate: createMutation } = useCreateProject({
    onSuccess: (createData) => {
      if (createData && createData.project) {
        const el = document.getElementById("search-input") as HTMLInputElement;
        if (el) {
          el.value = "";
        }
        refetchProjects();
        const { project } = createData;
        setSelectedTab(TabsType.sold);
        setIsAdvancedFilterOpen(true);
        setIsStreetViewOn(false);
        navigate(`${PATH.project}/${project.id}`);
      }
    },
  });

  useEffect(() => {
    if(id && projectsData?.project_summaries_response){
      const ids = projectsData?.project_summaries_response.map((item) => item.id);
      if(ids.indexOf(id) === -1){
        navigate(`${PATH.project}/${ids[0]}`);
      }
    }
  },[projectsData]);

  const { mutate: deleteMutation } = useDeleteProject({
    onSuccess: (data) => {
      refetchProjects({ throwOnError: true }).then((res) => {
        if (res.status === SUCCESS) {
          const projects = res.data?.project_summaries_response;
          if (!projects || projects.length === 0) createMutation();

          //if we deleted the project we are on we need to reset
          if (id === data.id) {
            resetProject();
            resetMap();
            if (projects && projects.length !== 0) {
              const firstProjectId = projects[0].id;
              navigate(`${PATH.project}/${firstProjectId}`);
            }
          }
        }
      });
    },
  });

  const { mutate: updateProjectNameMutation } = useUpdateProjectName({
    onSuccess: (data) => {
      refetchProjects();

      setProject((prevState) => ({
        ...prevState,
        name: data.name,
      }));
    },
  });

  useEffect(() => {
    if (!isPending) {
      const projects = projectsData?.project_summaries_response;
      if (!projects || projects.length === 0) createMutation();

      if (id === undefined) {
        //After first fetch of project, if we don't get an ID in params we should set the first project as default.
        const isOnProjectRoute =
          location.pathname.startsWith(PATH.project) ||
          location.pathname === '/';
        if (isOnProjectRoute && projects && projects.length !== 0) {
          const firstProjectId = projects[0].id;
          navigate(`${PATH.project}/${firstProjectId}`);
        }
      }
    }
  }, [isPending]);

  const handleProjectClick = (id: string) => {
    save(true);
    navigate(`${PATH.project}/${id}`);
  };

  const handleLogoutClick = () => {
    resetProject();
    resetMap(true);
    logout();
  };

  const updateProjectName = (newName: string) => {
    if (editName) {
      updateProjectNameMutation({
        id: editName,
        newName: newName,
      });
    }

    refetchProjects();
    setEditName(null);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    updateProjectName(newName);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const newName = e.currentTarget.value;
      updateProjectName(newName);
    } else if (e.key === 'Escape') {
      if (inputRef.current) {
        setEditName(null);
      }
    }
  };

  function openCookieConsent(){
    setIsCookieConsentOpen(true);
  }

  useEffect(() => {
    if (editName && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editName]);

  const topMenuData = {
    trigger: {
      img: ValueguardLogo,
      alt: 'Valueguard logo',
      text: strings.sidebar.valueService,
    },
    items: [
      {
        img: ValueguardLogo,
        alt: 'Valueguard logo',
        text: strings.sidebar.valueService,
      },
      {
        img: MapstatLogo,
        alt: 'Mapstat logo',
        text: strings.sidebar.mapStat,
        link: 'https://mapstat.valueguard.se/',
      },
    ],
  };

  const bottomMenuData = {
    trigger: {
      img: UserAvatar,
      alt: 'User avatar',
      text: userData ? userData.email : '',
      rounded: true,
    },
    items: [
      { text: strings.sidebar.savedValues, link: `${PATH.projects}` },
      { text: strings.sidebar.cookieConsent, handleClick: openCookieConsent},
      { text: strings.sidebar.terms, link: gdprPDF },
      {
        text: strings.sidebar.support,
        link: `mailto:${strings.login.emailUrl.email}`,
      },
      { text: strings.login.logout, handleClick: handleLogoutClick },
    ],
  };

  useEffect(() => {
    if (!isDialogOpen) setProjectToDelete('');
  }, [isDialogOpen]);

  const handleDeleteClick = () => {
    deleteMutation({ id: projectToDelete });
    setProjectToDelete('');
  };

  return (
    <StyledSidebar $isFolded={isFolded}>
      <CloseButton
        color={ButtonColor.default}
        icon={isFolded ? Icons.chevronRight : Icons.chevronLeft}
        onClick={() => setIsFolded(!isFolded)}
      />
      <TopContainer>
        <DropDown data={topMenuData} imgSize="large" />
      </TopContainer>
      <ProjectsContainer>
        <TopLine />
        <Heading $isFolded={isFolded}>
          {isFolded ? (
            <Button
              icon={Icons.plus}
              onClick={createMutation}
              color={ButtonColor.primary}
              size={ButtonSize.medium}
            />
          ) : (
            <Button
              icon={Icons.plus}
              className="w-full"
              onClick={createMutation}
              color={ButtonColor.primary}
              size={ButtonSize.large}
            >
              {strings.data.newSearch}
            </Button>

          )}
        </Heading>
        {isError && <div>{strings.sidebar.error}</div>}
        {!isError && (
          <ProjectsList>
            {!isFolded && projectsData && (
              <>
                {projectsData.project_summaries_response &&
                  projectsData.project_summaries_response
                    .slice(0, 25)
                    .map((valuation) => (
                      <ProjectItem
                        key={`project-${valuation.id}`}
                        $isSelected={valuation.id === id}>
                        {editName === valuation.id ? (
                          <Input
                            ref={inputRef}
                            type="text"
                            defaultValue={''}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                          />
                        ) : (
                          <>
                            <ProjectLink
                              $isSelected={valuation.id === id}
                              onClick={() => {
                                handleProjectClick(valuation.id);
                              }}>
                              {valuation.name || strings.data.noProjectName}
                            </ProjectLink>
                            <DropDown
                              data={{
                                trigger: {
                                  icon: Icons.dotMenu,
                                  rounded: true,
                                },
                                items: [
                                  {
                                    text: strings.sidebar.open,
                                    handleClick: () =>
                                      handleProjectClick(valuation.id),
                                  },
                                  {
                                    text: strings.sidebar.rename,
                                    handleClick: () => {
                                      setEditName(valuation.id);
                                    },
                                  },
                                  {
                                    text: strings.sidebar.delete,
                                    handleClick: () => {
                                      setIsDialogOpen(true);
                                      setProjectToDelete(valuation.id);
                                    },
                                  },
                                ],
                              }}
                            />
                            <Root
                              open={isDialogOpen}
                              onOpenChange={setIsDialogOpen}>
                              <Dialog
                                title={`${strings.sidebar.deleteHeading}`}
                                description={`${strings.sidebar.deleteDescription}`}
                                bottomButton={{
                                  text: strings.sidebar.delete,
                                  type: 'submit',
                                  onClick: handleDeleteClick,
                                }}
                              />
                            </Root>
                          </>
                        )}
                      </ProjectItem>
                    ))}
              </>
            )}
          </ProjectsList>
        )}
        <ButtomLine />
      </ProjectsContainer>
      <BottomContainer $isFolded={isFolded}>
        <DropDown data={bottomMenuData} bottomLine={true} imgSize="small" />
      </BottomContainer>
    </StyledSidebar>
  );
};

export default Sidebar;
